import { useState, useEffect } from 'react';
import { Table } from '@/UIlibrary/Table/Table';
import { GRAY_COLORS, BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { useTranslations } from 'use-intl';
import { ChevronDown, ChevronUp, AlertCircle, Search, Calendar, X } from 'react-feather';
import { Typography } from '@/stories/Library/Components/Typography/Typography';
import { useNavigate } from 'react-router-dom';
import Button from '@/UIlibrary/Button/Button';
import InputField from '@/UIlibrary/InputField/InputField';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import { formatDateTime } from '@/utils/utils';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { es } from 'date-fns/locale';
import { RequestStatusBadge } from './RequestStatusBadge';
import MultiSelectMicrocreditRequestStatus from './MultiSelectCreditRequestStatuses';
import {
	useVanCreditRequestsQuery,
	SortDirection,
	RequestSort,
	RequestFilter,
	OnboardingStatus,
	RequestStatus
} from '@/graphql/__generated__/van-credits';
import MultiSelectOnboardingStatus from '@/pages/Onboarding/MultiSelectOnboardingStatus';
import { OnboardingStatusBadge } from '@/pages/Onboarding/OnboardingStatusBadge';

type SortableField = 'startedAt' | 'status' | 'expectedDisbursementDate';

const PAGE_SIZE = 15;

const VanCreditRequestsTable = () => {
	const t = useTranslations('VanCreditRequests');
	const navigate = useNavigate();
	const vanCreditClient = useApolloClient(SCHEMAS.VAN_CREDITS);
	const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.DESC);
	const [sortField, setSortField] = useState<SortableField>('startedAt');
	const [searchInput, setSearchInput] = useState('');
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
	const [selectedStatuses, setSelectedStatuses] = useState<RequestStatus[]>([]);
	const [selectedOnboardingStatuses, setSelectedOnboardingStatuses] = useState<OnboardingStatus[]>([
		OnboardingStatus.APPROVED
	]);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [dateRange, setDateRange] = useState<{
		startDate: Date | undefined;
		endDate: Date | undefined;
		key: string;
	}>({
		startDate: undefined,
		endDate: undefined,
		key: 'selection'
	});

	// Debounce search term to avoid too many API calls
	useEffect(() => {
		const timerId = setTimeout(() => {
			setDebouncedSearchTerm(searchInput);
		}, 300);

		return () => clearTimeout(timerId);
	}, [searchInput]);

	const getFilter = (): RequestFilter | undefined => {
		const filter: RequestFilter = {};

		if (debouncedSearchTerm) {
			filter.searchTerm = debouncedSearchTerm;
		}

		if (selectedStatuses.length > 0) {
			filter.requestStatus = selectedStatuses;
		}

		if (selectedOnboardingStatuses.length > 0) {
			filter.onboardingStatus = selectedOnboardingStatuses;
		}

		if (dateRange.startDate || dateRange.endDate) {
			filter.startedAt = {};

			if (dateRange.startDate) {
				// Set start date to beginning of the day (00:00:00)
				const startDate = new Date(dateRange.startDate);
				startDate.setHours(0, 0, 0, 0);
				filter.startedAt.from = startDate.toISOString();
			}

			if (dateRange.endDate) {
				// Set end date to end of the day (23:59:59.999)
				const endDate = new Date(dateRange.endDate);
				endDate.setHours(23, 59, 59, 999);
				filter.startedAt.to = endDate.toISOString();
			}
		}

		// Return undefined if no filters applied
		return Object.keys(filter).length ? filter : undefined;
	};

	const getCurrentSort = (): RequestSort => {
		// Map UI sort fields to API sort fields (if different)
		const sortMapping: Record<SortableField, string> = {
			startedAt: 'startedAt',
			status: 'requestStatus',
			expectedDisbursementDate: 'expectedDisbursementDate'
		};

		return {
			[sortMapping[sortField]]: sortDirection
		};
	};

	// Using the query with filter and sort support
	const { loading, error, data, fetchMore, refetch } = useVanCreditRequestsQuery({
		client: vanCreditClient,
		variables: {
			first: PAGE_SIZE,
			sorts: [getCurrentSort()],
			filter: getFilter()
		},
		notifyOnNetworkStatusChange: true
	});

	// Effect to refetch when filters change
	useEffect(() => {
		refetch({
			first: PAGE_SIZE,
			sorts: [getCurrentSort()],
			filter: getFilter()
		});
	}, [
		debouncedSearchTerm,
		sortDirection,
		sortField,
		selectedStatuses,
		selectedOnboardingStatuses,
		dateRange,
		refetch
	]);

	const handleLoadMore = async () => {
		if (!data?.requests?.pageInfo.hasNextPage) return;

		const lastCursor = data.requests?.pageInfo.endCursor;
		if (!lastCursor) return;

		try {
			await fetchMore({
				variables: {
					after: lastCursor,
					first: PAGE_SIZE,
					sorts: [getCurrentSort()],
					filter: getFilter()
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.requests) return prev;

					return {
						...prev,
						requests: {
							...fetchMoreResult.requests,
							edges: [...(prev.requests?.edges || []), ...(fetchMoreResult.requests.edges || [])]
						}
					};
				}
			});
		} catch (error) {
			console.error('Error loading more van credit requests:', error);
		}
	};

	const handleDateRangeChange = (
		rangesByKey: Record<string, { startDate?: Date; endDate?: Date }>
	) => {
		const selection = rangesByKey.selection;
		if (selection?.startDate && selection?.endDate) {
			setDateRange({
				startDate: selection.startDate,
				endDate: selection.endDate,
				key: 'selection'
			});
			setShowDatePicker(false);
		}
	};

	const handleClearDateRange = () => {
		setDateRange({
			startDate: undefined,
			endDate: undefined,
			key: 'selection'
		});
	};

	const handleSortChange = (field: SortableField) => {
		if (field === sortField) {
			setSortDirection(
				sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
			);
		} else {
			setSortField(field);
			setSortDirection(SortDirection.DESC);
		}
	};

	const handleSearchSubmit = () => {
		// The debounce effect will handle the actual search
		console.log('Search term:', searchInput);
	};

	const handleClearSearch = () => {
		setSearchInput('');
		setDebouncedSearchTerm('');
	};

	const handleStatusFilterChange = (statuses: RequestStatus[]) => {
		setSelectedStatuses(statuses);
	};

	const handleOnboardingStatusFilterChange = (status: OnboardingStatus[]) => {
		setSelectedOnboardingStatuses(status);
	};

	const handleClearAllFilters = () => {
		setSearchInput('');
		setDebouncedSearchTerm('');
		setSelectedStatuses([]);
		setSelectedOnboardingStatuses([]);
		setDateRange({
			startDate: undefined,
			endDate: undefined,
			key: 'selection'
		});
	};

	const handleViewDetails = (requestId: string) => {
		navigate(`requests/${requestId}`);
	};

	const SortIcon = ({ field }: { field: SortableField }) => {
		const isActive = field === sortField;

		if (isActive) {
			const Icon = sortDirection === SortDirection.ASC ? ChevronUp : ChevronDown;
			return <Icon size={18} style={{ color: BRAND_COLORS.brand600 }} />;
		}

		return (
			<div className="flex flex-col">
				<ChevronUp size={12} style={{ color: BRAND_COLORS.brand300, marginBottom: '-6px' }} />
				<ChevronDown size={12} style={{ color: BRAND_COLORS.brand300 }} />
			</div>
		);
	};

	const renderRequestStatus = (status: RequestStatus) => (
		<RequestStatusBadge status={status} size="sm" />
	);

	const renderOnboardingStatus = (request: any) => (
		<OnboardingStatusBadge status={request.onboardingStatus as OnboardingStatus} size="sm" />
	);

	const renderFullName = (request: {
		names: string;
		firstLastName: string;
		secondLastName: string;
	}) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{[request.names, request.firstLastName, request.secondLastName]
				.filter(Boolean)
				.join(' ')
				.trim() || '-'}
		</Typography>
	);

	const renderEmail = (request: { email: string }) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{request.email || '-'}
		</Typography>
	);

	const renderPhoneNumber = (request: { phoneNumber: string }) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{request.phoneNumber || '-'}
		</Typography>
	);

	const renderDate = (date: string) => (
		<Typography size="sm" color={GRAY_COLORS.gray700}>
			{formatDateTime(date) || '-'}
		</Typography>
	);

	const renderVehicles = (details: { vehiclesRequested: string }) => {
		const vehicles = details?.vehiclesRequested;
		return (
			<Typography size="sm" color={GRAY_COLORS.gray700}>
				{vehicles || '-'}
			</Typography>
		);
	};

	const renderViewDetailsButton = (request: { id: string }) => (
		<Button textOnly onClick={() => handleViewDetails(request.id)} label={t('ViewDetails')} />
	);

	const getRequestStatusDisplayName = (status: RequestStatus) => {
		const statusMap = {
			[RequestStatus.CREATED]: t('CreatedStatus'),
			[RequestStatus.PERSONAL_INFORMATION_REGISTERED]: t('PersonalInformationStatus'),
			[RequestStatus.ACTIVITY_INFORMATION_REGISTERED]: t('BusinessInformationStatus'),
			[RequestStatus.CREDIT_DETAILS_REGISTERED]: t('CreditDetailsStatus'),
			[RequestStatus.MONTHLY_INCOME_INFORMATION_REGISTERED]: t('IncomeInformationStatus'),
			[RequestStatus.MONTHLY_BUSINESS_EXPENSES_REGISTERED]: t('BusinessExpensesStatus'),
			[RequestStatus.MONTHLY_HOUSE_EXPENSES_REGISTERED]: t('HouseExpensesStatus'),
			[RequestStatus.VALUE_OF_ASSETS_REGISTERED]: t('ValueOfAssetsStatus'),
			[RequestStatus.OTHER_CREDITS_REGISTERED]: t('OtherCreditsStatus'),
			[RequestStatus.EXPECTED_PROFIT_FOR_NEW_VEHICLES_REGISTERED]: t('ExpectedProfitStatus'),
			[RequestStatus.IN_REVIEW]: t('InReviewStatus'),
			[RequestStatus.COMPLETED]: t('CompletedStatus'),
			[RequestStatus.REJECTED]: t('RejectedStatus'),
			[RequestStatus.PASSED_TO_CREDIT_COMMITTEE]: t('PassedToCreditCommitteeStatus'),
			[RequestStatus.APPROVED_BY_CREDIT_COMMITTEE]: t('ApprovedByCreditCommitteeStatus'),
			[RequestStatus.REJECTED_BY_CREDIT_COMMITTEE]: t('RejectedByCreditCommitteeStatus'),
			[RequestStatus.SIGNED_DOCUMENTS_UPLOADED]: t('SignedDocumentsUploadedStatus')
		};

		return statusMap[status] || t('Status');
	};

	const getOnboardingStatusText = (status: OnboardingStatus): string => {
		const statusMap: Record<OnboardingStatus, string> = {
			[OnboardingStatus.IN_PROCESS]: t('InProcess'),
			[OnboardingStatus.CORRECTIONS_REQUESTED]: t('CorrectionsRequested'),
			[OnboardingStatus.PENDING_REVIEW]: t('PendingReview'),
			[OnboardingStatus.REJECTED]: t('Rejected'),
			[OnboardingStatus.APPROVED]: t('Approved')
		};
		return statusMap[status] || t('InProcess');
	};

	const columns = [
		{
			header: '',
			key: 'actions',
			cellRenderer: (_: any, request: any) => renderViewDetailsButton(request)
		},
		{
			header: t('Name'),
			key: 'names',
			cellRenderer: (_: any, request: any) => renderFullName(request)
		},
		{
			header: t('Email'),
			key: 'email',
			cellRenderer: (_: any, request: any) => renderEmail(request)
		},
		{
			header: t('PhoneNumber'),
			key: 'phoneNumber',
			cellRenderer: (_: any, request: any) => renderPhoneNumber(request)
		},
		{
			header: t('VehicleRequested'),
			key: 'creditDetails',
			cellRenderer: (_: any, request: { creditDetails: any }) =>
				renderVehicles(request.creditDetails)
		},
		{
			header: (
				<div
					className="flex items-center gap-2 cursor-pointer hover:text-gray-700 transition-colors"
					onClick={() => handleSortChange('startedAt')}
					role="button"
					tabIndex={0}
				>
					<span>{t('StartedAt')}</span>
					<SortIcon field="startedAt" />
				</div>
			),
			key: 'startedAt',
			cellRenderer: (_: any, request: any) => renderDate(request.startedAt || request.insertedAt)
		},
		{
			header: (
				<div
					className="flex items-center gap-2 cursor-pointer hover:text-gray-700 transition-colors"
					onClick={() => handleSortChange('expectedDisbursementDate')}
					role="button"
					tabIndex={0}
				>
					<span>{t('ExpectedDisbursementDate')}</span>
					<SortIcon field="expectedDisbursementDate" />
				</div>
			),
			key: 'expectedDisbursementDate',
			cellRenderer: (_: any, request: any) => renderDate(request.expectedDisbursementDate)
		},
		{
			header: (
				<div className="flex items-center gap-2 cursor-pointer" tabIndex={0}>
					<span>{t('OnboardingStatus')}</span>
				</div>
			),
			key: 'onboardingStatus',
			cellRenderer: (_: any, request: any) => renderOnboardingStatus(request)
		},
		{
			header: (
				<div
					className="flex items-center gap-2 cursor-pointer hover:text-gray-700 transition-colors"
					onClick={() => handleSortChange('status')}
					role="button"
					tabIndex={0}
				>
					<span>{t('CreditStatus')}</span>
					<SortIcon field="status" />
				</div>
			),
			key: 'status',
			cellRenderer: (_: any, request: any) => renderRequestStatus(request.requestStatus)
		}
	];

	if (error) {
		return (
			<div className="flex items-center gap-2 p-4 text-red-600 bg-red-50 rounded-md">
				<AlertCircle size={16} />
				<Typography size="sm">{t('ErrorLoadingVanCreditRequests')}</Typography>
			</div>
		);
	}

	const isInitialLoading = loading && !data;
	const requests =
		data?.requests?.edges
			?.map((edge) => edge?.node)
			.filter((node) => node !== null && node !== undefined) || [];
	const totalCount = data?.requests?.count || 0;
	const hasMore = data?.requests?.pageInfo.hasNextPage || false;

	// Define search action buttons based on whether search is active
	const searchActions = searchInput ? (
		<div className="flex items-center">
			<Button
				label={''}
				endIcon={<X size={18} />}
				onClick={handleClearSearch}
				textOnly
				size="auto"
				data-testid="clear-search-button"
			/>
		</div>
	) : (
		<Button
			label={''}
			textOnly
			onClick={handleSearchSubmit}
			data-testid="search-button"
			endIcon={<Search size={20} />}
		/>
	);

	const statusOptions = Object.values(RequestStatus).map((status) => ({
		value: status,
		label: `${getRequestStatusDisplayName(status)}`
	}));

	const onboardingStatusOptions = Object.values(OnboardingStatus).map((status) => ({
		value: status,
		label: `${getOnboardingStatusText(status)}`
	}));

	return (
		<div className="flex flex-col py-4 gap-4">
			{/* Search and filter section */}
			<div className="flex flex-col md:flex-row w-full items-center gap-8">
				<div className="flex gap-4 mt-5 w-full md:w-auto">
					<InputField
						onChange={(e) => setSearchInput(e.target.value)}
						value={searchInput}
						shape="pill"
						placeholder={t('Search')}
						dataTestId="searchInput"
						endAction={searchActions}
					/>
				</div>

				<div className="flex gap-4 items-center w-full md:w-auto">
					<MultiSelectOnboardingStatus
						selectedStatuses={selectedOnboardingStatuses}
						onChange={handleOnboardingStatusFilterChange}
						statusOptions={onboardingStatusOptions}
						placeholder={t('SelectAnOption')}
						label={t('SelectOnboardingStatuses')}
					/>
				</div>
				<div className="flex gap-4 items-center w-full md:w-auto">
					<MultiSelectMicrocreditRequestStatus
						selectedStatuses={selectedStatuses}
						onChange={handleStatusFilterChange}
						statusOptions={statusOptions}
						placeholder={t('SelectAnOption')}
						label={t('SelectCreditStatuses')}
					/>
				</div>
			</div>

			{/* Date picker section */}
			<div className="flex flex-wrap gap-2 mb-4 items-center">
				<div className="relative">
					<Button
						onClick={() => setShowDatePicker(!showDatePicker)}
						size="auto"
						endIcon={<Calendar size={20} />}
						label={
							dateRange.startDate && dateRange.endDate
								? `${formatDateTime(dateRange.startDate.toISOString()).split(' ')[0]} - ${formatDateTime(dateRange.endDate.toISOString()).split(' ')[0]}`
								: t('SelectDateRange')
						}
					/>

					<span className="ml-5">
						{dateRange.startDate && (
							<Button onClick={handleClearDateRange} size="auto" textOnly label={t('ClearDates')} />
						)}
					</span>

					{showDatePicker && (
						<div className="absolute z-50 mt-2 bg-white shadow-lg rounded-lg">
							<DateRange
								showMonthAndYearPickers={true}
								showDateDisplay={false}
								ranges={[dateRange]}
								months={2}
								onChange={handleDateRangeChange}
								locale={es}
								moveRangeOnFirstSelection={false}
								direction="horizontal"
								rangeColors={[BRAND_COLORS.brand600]}
								minDate={new Date(2020, 0, 1)}
								maxDate={new Date()}
							/>
						</div>
					)}
				</div>

				{(searchInput ||
					selectedStatuses.length > 0 ||
					dateRange.startDate ||
					selectedOnboardingStatuses.length > 0) && (
					<Button
						onClick={handleClearAllFilters}
						size="auto"
						textOnly
						data-testid="clear-all-filters-button"
						label={t('ClearAllFilters')}
					/>
				)}
			</div>

			<div className="custom-scrollbar max-h-[50vh] overflow-y-auto">
				{isInitialLoading ? (
					<div className="flex justify-center items-center h-64">
						<div className="loader" data-testid="loader" />
					</div>
				) : (
					<>
						<Table dataTestId="VanCreditRequestsTable" columns={columns} data={requests} />

						{hasMore && (
							<div className="text-center py-4">
								<Button
									onClick={handleLoadMore}
									size="auto"
									label={t('LoadMore')}
									disabled={loading}
								/>
							</div>
						)}

						{!loading && requests.length === 0 && (
							<div className="text-center py-4 text-gray-500">{t('NoResults')}</div>
						)}
					</>
				)}
			</div>

			{loading && !isInitialLoading && (
				<div className="flex justify-center py-4">
					<div className="loader" />
				</div>
			)}

			{!isInitialLoading && totalCount > 0 && (
				<div className="mt-1 ml-5 border-t border-b py-3">
					<Typography size="sm" color={GRAY_COLORS.gray600}>
						{t('ShowingResults')}{' '}
						<span className="font-medium">
							{requests.length.toLocaleString()} {t('Of')} {totalCount.toLocaleString()}
						</span>
					</Typography>
				</div>
			)}
		</div>
	);
};

export default VanCreditRequestsTable;
